import React, { useState, createContext, useContext } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';

interface TabsContextType {
  activeTab: string;
  setActiveTab: (value: string) => void;
}

const TabsContext = createContext<TabsContextType | null>(null);

interface TabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  className?: string;
  children?: React.ReactNode;
}

export const Tabs: React.FC<TabsProps> = ({ defaultValue, value, onValueChange, className, children }) => {
  const [activeTab, setActiveTab] = useState(defaultValue || '');

  const currentValue = value !== undefined ? value : activeTab;

  const handleTabChange = (newValue: string) => {
    if (value === undefined) {
      setActiveTab(newValue);
    }
    onValueChange?.(newValue);
  };

  return (
    <TabsContext.Provider value={{ activeTab: currentValue, setActiveTab: handleTabChange }}>
      <div className={cn('w-full', className)}>{children}</div>
    </TabsContext.Provider>
  );
};

interface TabsListProps {
  className?: string;
  children?: React.ReactNode;
  /** pill = segmented control; underline = classic tabs */
  variant?: 'underline' | 'pill';
  /** Shrink tab bar to content width (pill variant) */
  fit?: boolean;
}

export const TabsList: React.FC<TabsListProps> = ({ className, children, variant = 'underline', fit = false }) => {
  const { colors } = useTheme();

  if (variant === 'pill') {
    return (
      <div
        className={cn(
          'flex gap-1 rounded-lg border p-1',
          fit && 'inline-flex w-fit max-w-full',
          colors.border,
          colors.headerBg,
          className,
        )}
        role="tablist"
      >
        {children}
      </div>
    );
  }

  return (
    <div className={cn('flex border-b', colors.border, className)} role="tablist">
      {children}
    </div>
  );
};

type TabCountTone = 'muted' | 'accent' | 'warning' | 'onColor';
export type TabColorScheme = 'indigo' | 'violet' | 'sky' | 'emerald' | 'amber';

type TabSchemeClasses = {
  active: string;
  inactive: string;
  badgeActive: string;
  badgeInactive: string;
  ring: string;
  iconInactive: string;
};

const getTabScheme = (scheme: TabColorScheme, isDark: boolean): TabSchemeClasses => {
  const inactiveBase = isDark
    ? 'text-slate-400 hover:bg-white/[0.06] hover:text-slate-200'
    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-800';

  switch (scheme) {
    case 'indigo':
      return {
        active: 'bg-indigo-600 text-white shadow-md shadow-indigo-600/30',
        inactive: inactiveBase,
        badgeActive: 'bg-white/25 text-white',
        badgeInactive: isDark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700',
        ring: 'focus-visible:ring-indigo-500/50',
        iconInactive: isDark ? 'bg-white/[0.08]' : 'bg-slate-200/70',
      };
    case 'violet':
      return {
        active: 'bg-violet-600 text-white shadow-md shadow-violet-600/30',
        inactive: inactiveBase,
        badgeActive: 'bg-white/25 text-white',
        badgeInactive: isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-700',
        ring: 'focus-visible:ring-violet-500/50',
        iconInactive: isDark ? 'bg-white/[0.08]' : 'bg-slate-200/70',
      };
    case 'sky':
      return {
        active: 'bg-sky-600 text-white shadow-md shadow-sky-600/30',
        inactive: inactiveBase,
        badgeActive: 'bg-white/25 text-white',
        badgeInactive: isDark ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-100 text-sky-700',
        ring: 'focus-visible:ring-sky-500/50',
        iconInactive: isDark ? 'bg-white/[0.08]' : 'bg-slate-200/70',
      };
    case 'emerald':
      return {
        active: 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30',
        inactive: inactiveBase,
        badgeActive: 'bg-white/25 text-white',
        badgeInactive: isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700',
        ring: 'focus-visible:ring-emerald-500/50',
        iconInactive: isDark ? 'bg-white/[0.08]' : 'bg-slate-200/70',
      };
    case 'amber':
      return {
        active: 'bg-amber-500 text-white shadow-md shadow-amber-500/35',
        inactive: inactiveBase,
        badgeActive: 'bg-white/25 text-white',
        badgeInactive: isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-800',
        ring: 'focus-visible:ring-amber-500/50',
        iconInactive: isDark ? 'bg-white/[0.08]' : 'bg-slate-200/70',
      };
  }
};

const tabCountBadgeClass = (
  isActive: boolean,
  tone: TabCountTone,
  isDark: boolean,
  scheme?: TabColorScheme,
) => {
  if (scheme) {
    const s = getTabScheme(scheme, isDark);
    return isActive ? s.badgeActive : s.badgeInactive;
  }
  if (tone === 'warning') {
    return isActive
      ? 'bg-white/25 text-white'
      : isDark
        ? 'bg-amber-500/20 text-amber-200'
        : 'bg-amber-100 text-amber-800';
  }
  if (tone === 'accent') {
    return isActive
      ? 'bg-white/25 text-white'
      : isDark
        ? 'bg-indigo-500/20 text-indigo-300'
        : 'bg-indigo-100 text-indigo-700';
  }
  if (tone === 'onColor' && isActive) {
    return 'bg-white/25 text-white';
  }
  return isActive
    ? isDark
      ? 'bg-slate-600/80 text-slate-100'
      : 'bg-slate-200/90 text-slate-700'
    : isDark
      ? 'bg-white/[0.08] text-slate-400'
      : 'bg-slate-100 text-slate-500';
};

interface TabsTriggerProps {
  value: string;
  className?: string;
  disabled?: boolean;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  count?: number;
  countTone?: TabCountTone;
  showPendingDot?: boolean;
  /** @deprecated use TabsList variant="pill" */
  appearance?: 'underline' | 'pill';
  /** Colored active state (best with TabsList variant="pill") */
  colorScheme?: TabColorScheme;
  /** Tighter padding and text (pill tabs) */
  size?: 'sm' | 'md';
}

export const TabsTrigger: React.FC<TabsTriggerProps> = ({
  value,
  className,
  disabled = false,
  children,
  icon,
  count,
  countTone = 'muted',
  showPendingDot = false,
  appearance = 'underline',
  colorScheme,
  size = 'md',
}) => {
  const context = useContext(TabsContext);
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';

  if (!context) {
    throw new Error('TabsTrigger must be used within Tabs');
  }

  const { activeTab, setActiveTab } = context;
  const isActive = activeTab === value;
  const showCount = count != null && count > 0;
  const pill = appearance === 'pill' || Boolean(colorScheme);
  const scheme = colorScheme ? getTabScheme(colorScheme, isDark) : null;
  const compact = size === 'sm';

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={() => !disabled && setActiveTab(value)}
      disabled={disabled}
      className={cn(
        'relative inline-flex shrink-0 items-center transition-all duration-200',
        compact ? 'gap-1.5 text-xs font-semibold' : 'gap-2 text-sm font-semibold',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
        isDark ? 'focus-visible:ring-offset-[#080d18]' : 'focus-visible:ring-offset-white',
        pill
          ? cn(
              'justify-center rounded-md border-b-0',
              compact ? 'px-2 py-1.5' : 'px-3 py-2',
            )
          : 'px-4 py-2 border-b-2 border-transparent -mb-px',
        scheme
          ? cn(isActive ? scheme.active : scheme.inactive, scheme.ring)
          : cn(
              pill && isActive && cn(colors.surface, 'shadow-sm ring-1', colors.border),
              !pill && isActive && colors.primaryBorderAll,
              isActive ? colors.text : colors.textSecondary,
              !disabled && !isActive && colors.interactiveHover,
            ),
        disabled && 'cursor-not-allowed opacity-50',
        className,
      )}
    >
      {icon && (
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded',
            compact ? 'h-5 w-5' : 'h-7 w-7 rounded-md',
            isActive && scheme && 'bg-white/20',
            !isActive && scheme && scheme.iconInactive,
          )}
        >
          {icon}
        </span>
      )}
      <span className="truncate">{children}</span>
      {showCount && (
        <span
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-full font-bold tabular-nums leading-none',
            compact ? 'h-4 min-w-[1rem] px-1 text-[10px]' : 'h-5 min-w-[1.25rem] px-1.5 text-[11px]',
            tabCountBadgeClass(isActive, countTone, isDark, colorScheme),
          )}
        >
          {count}
        </span>
      )}
      {showPendingDot && !isActive && (
        <span
          className={cn(
            'absolute right-2 top-2 h-2 w-2 animate-pulse rounded-full bg-amber-400 ring-2',
            isDark ? 'ring-[#0e1524]' : 'ring-white',
          )}
          aria-hidden
        />
      )}
    </button>
  );
};

interface TabsContentProps {
  value: string;
  className?: string;
  children?: React.ReactNode;
}

export const TabsContent: React.FC<TabsContentProps> = ({ value, className, children }) => {
  const context = useContext(TabsContext);

  if (!context) {
    throw new Error('TabsContent must be used within Tabs');
  }

  const { activeTab } = context;

  if (activeTab !== value) return null;

  return (
    <div className={cn('mt-4 focus:outline-none', className)} role="tabpanel">
      {children}
    </div>
  );
};

const TabsComponent = {
  Root: Tabs,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
};

export default TabsComponent;
